import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, SUPPORTED_LANGUAGES } from "@/hooks/useTranslation";
import {
  Bird, ChevronRight, Check, ArrowRight, Award
} from "lucide-react";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import { LegalPage, TermsContent, PrivacyContent, RefundContent, ContactContent, GDPRContent } from "./LandingLegal";
import {
  EditorialSections, SocialProofSection, TestimonialSection,
  VoiceFeaturesSection, PricingSection, FAQSection, CTASection,
  LandingFooter, TeamAssemblySection
} from "./LandingSections";

const PLAN_FEATURES = {
  trial: ["feat_full_access_24h", "feat_1_hub", "feat_3_drivers", "feat_all_features", "feat_ai_included", "feat_no_card", "feat_gdpr_basic"],
  pro: ["feat_1_hub_pro", "feat_5_drivers", "feat_unlimited_sites", "feat_ai_50", "feat_pdf_import", "feat_pdf_export", "feat_calendar", "feat_route_plan", "feat_email_support", "feat_voice_notifications", "feat_gdpr_full", "feat_data_export"],
  business: ["feat_everything_pro", "feat_10_hubs", "feat_15_drivers", "feat_ai_unlimited", "feat_transport_map", "feat_route_history", "feat_leave_mgmt", "feat_service_portals", "feat_site_phases", "feat_priority_support", "feat_gdpr_full", "feat_voice_dictation", "feat_activity_pdf", "feat_data_export", "feat_consent_mgmt"],
};
const PLAN_LOCKED = {
  trial: ["feat_transport_map", "feat_ai_unlimited", "feat_service_portals"],
  pro: ["feat_transport_map", "feat_route_history", "feat_service_portals", "feat_site_phases"],
  business: [],
};
const PLANS_BASE = [
  { id: "trial", price: 0, currency: "EUR" },
  { id: "pro", price: 49, currency: "EUR", highlight: true },
  { id: "business", price: 149, currency: "EUR" },
];

export default function LandingPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const pricingRef = useRef(null);
  const [selectedLang, setSelectedLang] = useState(() => localStorage.getItem("pigeon-lang") || null);
  const [showZeroGate, setShowZeroGate] = useState(() => !localStorage.getItem("pigeon-lang"));
  const { t } = useTranslation(selectedLang || "en");

  const PRICING = PLANS_BASE.map((plan) => ({
    ...plan,
    name: t(`plan_${plan.id}_name`),
    cta: t(`plan_${plan.id}_cta`),
    period: plan.price > 0 ? t(`plan_${plan.id}_period`) : "",
    features: (PLAN_FEATURES[plan.id] || []).map((k) => t(k)),
    locked: (PLAN_LOCKED[plan.id] || []).map((k) => t(k)),
  }));

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setPaymentStatus("timeout"); return; }
    setIsCheckingPayment(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === "paid") { setPaymentStatus("success"); setIsCheckingPayment(false); return; }
      if (data.status === "expired") { setPaymentStatus("expired"); setIsCheckingPayment(false); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      if (attempts < 4) setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      else { setPaymentStatus("error"); setIsCheckingPayment(false); }
    }
  };

  const handleCheckout = async (packageId) => {
    try {
      const origin = window.location.origin;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout?package_id=${packageId}&origin_url=${encodeURIComponent(origin)}`, { method: 'POST' });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch { void 0; }
  };

  if (activePage === "terms") return <LegalPage title="Terms of Service" onBack={() => setActivePage("home")} content={<TermsContent />} />;
  if (activePage === "privacy") return <LegalPage title="Privacy Policy" onBack={() => setActivePage("home")} content={<PrivacyContent />} />;
  if (activePage === "refund") return <LegalPage title="Refund Policy" onBack={() => setActivePage("home")} content={<RefundContent />} />;
  if (activePage === "contact") return <LegalPage title="Contact Us" onBack={() => setActivePage("home")} content={<ContactContent />} />;
  if (activePage === "gdpr") return <LegalPage title="GDPR & Cookie Policy" onBack={() => setActivePage("home")} content={<GDPRContent />} />;

  if (showZeroGate) {
    return (
      <div className="min-h-[100dvh] bg-white flex flex-col items-center justify-center p-6 relative overflow-hidden" data-testid="zero-gate">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(220,38,38,0.03)_0%,transparent_70%)]" />
        <div className="relative z-10 w-full max-w-lg text-center animate-fadeInUp">
          <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
            <Bird className="w-10 h-10 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl md:text-5xl tracking-tighter text-slate-900 mb-3">
            <span className="font-light">Pigeon</span> <span className="font-black">Supply</span>
          </h1>
          <p className="text-base text-slate-500 font-light mb-10">{t("choose_language")}</p>
          <div className="grid grid-cols-3 gap-3 mb-8">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button key={lang.code} data-testid={`zero-gate-lang-${lang.code}`}
                onClick={() => setSelectedLang(lang.code)}
                className={`p-4 rounded-xl text-center transition-all border-2 text-sm tracking-tight ${
                  selectedLang === lang.code
                    ? "bg-red-50 border-red-500 text-red-700 shadow-sm font-bold"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 font-medium"
                }`}>
                <span className="block text-xs font-black mb-0.5 opacity-40">{lang.flag}</span>
                <span className="block text-sm">{lang.label}</span>
              </button>
            ))}
          </div>
          <button data-testid="zero-gate-continue-btn"
            onClick={() => {
              const lang = selectedLang || "en";
              localStorage.setItem("pigeon-lang", lang);
              document.documentElement.lang = lang;
              document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
              setShowZeroGate(false);
            }}
            className="btn-industrial w-full py-4 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-black shadow-lg transition-all">
            {t("continue_lang")} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white text-slate-900" data-testid="landing-page">
      {paymentStatus === "success" && (
        <div className="bg-red-600 text-slate-950 py-4 px-6 text-center font-bold text-sm uppercase tracking-wider animate-slideInDown" data-testid="payment-success">
          <Check className="w-5 h-5 inline mr-2" /> {t("payment_success")}
        </div>
      )}
      {isCheckingPayment && (
        <div className="bg-amber-500 text-slate-950 py-3 px-6 text-center font-bold text-sm uppercase tracking-wider animate-pulse">
          {t("verifying_payment")}
        </div>
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center shadow-md">
              <Bird className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <span className="text-lg font-black tracking-tighter uppercase">Pigeon Supply</span>
              <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-slate-400">{t("supply_platform")}</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => pricingRef.current?.scrollIntoView({ behavior: "smooth" })} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors">{t("nav_pricing")}</button>
            <button onClick={() => nav("/install")} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors flex items-center gap-1" data-testid="nav-install-btn">{t("install_app")}</button>
            <button onClick={() => setActivePage("contact")} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors">{t("nav_contact")}</button>
            <LanguageSwitcher compact />
            <button data-testid="nav-signin-btn" onClick={() => nav("/auth?mode=signin")} className="btn-industrial px-5 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-xl text-xs font-black hover:border-red-300 transition-colors">
              {t("auth_tab_signin") || "Sign In"}
            </button>
            <button data-testid="nav-launch-btn" onClick={() => nav("/auth")} className="btn-industrial px-5 py-2.5 bg-red-600 text-white rounded-xl text-xs font-black shadow-sm hover:bg-red-500 transition-colors">
              {t("launch_app")} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden pt-24 pb-32 px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(220,38,38,0.04)_0%,transparent_50%)]" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-2 mb-10 animate-fadeInUp">
            <Award className="w-4 h-4 text-red-500" />
            <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">{t("hero_badge")}</span>
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.85] mb-8 animate-fadeInUp stagger-1">
            <span className="font-light text-slate-400">{t("hero_line1")}</span><br />
            <span className="font-black text-slate-900">{t("hero_line2")}</span>{" "}
            <span className="font-black text-red-600">{t("hero_line3")}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-500 max-w-2xl font-light leading-relaxed mb-12 animate-fadeInUp stagger-2">
            {t("hero_desc")}
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-4 animate-fadeInUp stagger-3">
            <button data-testid="hero-start-btn" onClick={() => nav("/auth")}
              className="btn-industrial px-10 py-5 bg-red-600 text-white rounded-xl text-sm font-black shadow-lg hover:bg-red-500 hover:shadow-xl transition-all">
              {t("start_free_trial")} <ArrowRight className="w-5 h-5" />
            </button>
            <button onClick={() => pricingRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="btn-industrial px-10 py-5 bg-white border-2 border-slate-200 text-slate-700 rounded-xl text-sm font-black hover:border-red-300 transition-all">
              {t("view_pricing")}
            </button>
          </div>
        </div>
      </section>

      <SocialProofSection t={t} />
      <EditorialSections t={t} nav={nav} />
      <TestimonialSection t={t} />
      <TeamAssemblySection t={t} />
      <VoiceFeaturesSection t={t} />
      <PricingSection pricing={PRICING} pricingRef={pricingRef} t={t} nav={nav} handleCheckout={handleCheckout} />
      <CTASection t={t} nav={nav} />
      <FAQSection t={t} />
      <LandingFooter t={t} nav={nav} pricingRef={pricingRef} setActivePage={setActivePage} />
    </div>
  );
}
